import { useEffect, useState } from 'react';
import {
  applyTheme,
  nextThemeMode,
  readThemeMode,
  saveThemeMode,
  watchSystemTheme,
  type ThemeMode,
} from '@/lib/theme';

/**
 * 테마 전환 버튼. (00-architecture.md 1.2절)
 *
 * 버튼 하나로 light → dark → system 을 순환한다.
 * 설정 페이지를 따로 만들지 않았다 — 항목이 하나뿐이다.
 *
 * 🔴 초기 상태를 여기서 정하지 않는다
 *   index.html 의 인라인 스크립트가 React 보다 먼저 .dark 를 붙여둔다.
 *   여기서는 그 결과를 이어받아 "지금 무엇이 골라져 있는지" 를 표시하고
 *   사용자가 누르면 바꿀 뿐이다.
 *   여기서 처음 적용하면 다크 사용자에게 흰 화면이 한 번 번쩍인다.
 */

const LABEL: Record<ThemeMode, { icon: string; text: string }> = {
  light: { icon: '☀️', text: '밝게' },
  dark: { icon: '🌙', text: '어둡게' },
  system: { icon: '🖥️', text: '시스템' },
};

export function ThemeToggle() {
  const [mode, setMode] = useState<ThemeMode>(readThemeMode);

  // OS 설정이 바뀌면 따라간다. system 모드일 때만 의미가 있다.
  // 없으면 폰이 밤에 자동으로 야간 모드로 바뀌어도 열어둔 탭은 밝은 채로 남는다.
  useEffect(() => {
    if (mode !== 'system') {
      return;
    }
    return watchSystemTheme(() => applyTheme('system'));
  }, [mode]);

  function cycle() {
    const next = nextThemeMode(mode);
    setMode(next);
    saveThemeMode(next);
  }

  const { icon, text } = LABEL[mode];

  return (
    <button
      type="button"
      onClick={cycle}
      // 무엇으로 바뀔지가 아니라 지금 무엇인지를 읽어준다.
      // 스크린리더 사용자는 아이콘을 볼 수 없다.
      aria-label={`화면 테마 — 현재 ${text}. 누르면 바뀝니다`}
      title={`화면 테마 · ${text}`}
      className="inline-flex min-h-[36px] min-w-[36px] items-center justify-center rounded-lg text-base hover:bg-surface-hover"
    >
      <span aria-hidden="true">{icon}</span>
    </button>
  );
}
